
import React, { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { getStandardizedSignaturesForGeneralForms } from '../../../utils/formHelpers';
import { patientConsentSchema } from './general-forms.validation';
import EvaluationInput from '../../EvaluationNew/components/EvaluationInput';
import EvaluationFormHeader from '../../EvaluationNew/components/EvaluationFormHeader';
import { useApplicationFormLogic } from '../../../hooks/useApplicationFormLogic';
import { request } from '../../../utils/api';
import { toIsoDate, parseIsoDateToMDYUS } from '../../../utils/dateUtils';
import { mergeFormDataWithStatusExtras } from '../../../utils/applicationFormPayload';
import CloseButton from '../../../components/Application/CloseButton';
import GeneralFormFooter from './components/GeneralFormFooter';
import PropTypes from 'prop-types';

const SectionHeader = ({ title }) => (
    <h3 className="text-lg font-bold text-gray-800 border-b pb-2 mb-4 mt-8">{title}</h3>
);

SectionHeader.propTypes = {
    title: PropTypes.string
};

const CheckboxField = ({ name, label, register }) => (
    <div className="flex items-start gap-3 py-2">
        <input
            type="checkbox"
            id={name}
            {...register(name)}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <label htmlFor={name} className="text-sm text-gray-700 leading-relaxed cursor-pointer select-none">
            {label}
        </label>
    </div>
);

CheckboxField.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    register: PropTypes.func
};

const PatientConsent = ({ onClose }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = !!id;
    const [consentId, setConsentId] = useState(null);
    const [currentStatus, setCurrentStatus] = useState("");

    const methods = useForm({
        resolver: zodResolver(patientConsentSchema),
        defaultValues: {
            tenantId: undefined,
            userId: undefined,
            employeeName: "",
            applicantName: "",
            patientId: 10,

            // Consent Sections
            consentAdmissionTreatment: false,
            consentAuthorizationReleaseInfo: false,
            consentPatientCertificationPayment: false,
            consentHomeHealthBillingPps: false,
            consentAssignmentInsuranceBenefits: false,
            consentCmsAdvanceBeneficiaryNotice: false,

            // Payment
            paymentPrivateInsurance: false,
            paymentSelfPay: false,

            // Visit Frequency
            snVisits: "",
            chhaVisits: "",
            ptVisits: "",
            otVisits: "",
            stVisits: "",
            mswVisits: "",

            // Certification
            certPatientsRightsResponsibilities: false,
            certOasisPrivacyHippa: false,
            certStateJointCommissionHotline: false,
            certPatientsPrivacyRightsComplaint: false,
            certInitialPlanOfCareReviewed: false,
            certHomeHealthRatesReviewed: false,
            certStateJointCommissionHotlineInformed: false,
            certEmergencyPlanSafetyInstructions: false,
            certBrochureNonDiscriminatoryNotice: false,
            certReceivedCopyOfDocument: false,

            advanceDirectives: "Yes",
            photoRecordsAuthorization: false,

            patientSignature: "",
            patientName: "",
            patientSignedAt: "",
            applicantSignature: "",
            applicantSignedAt: "",
            reviewedBySignature: "",
            reviewedBySignedAt: "",
            reviewedBySignedBy: ""
        },
        mode: "onChange"
    });

    const { handleSubmit, watch, reset } = methods;

    const onSubmit = async (data, flag = 'completed', additionalData = {}) => {
        const actualFlag = typeof flag === 'string' ? flag : 'completed';


        try {
            // Ensure both name fields are synced for backend compatibility
            const merged = mergeFormDataWithStatusExtras(data, additionalData);
            const fullName = merged.employeeName || merged.applicantName || userFullName;

            const applicantSignedAtForPatient = merged.applicantSignedAt || parseIsoDateToMDYUS(new Date().toISOString());
            const payload = {
                ...merged,
                employeeName: fullName,
                applicantName: fullName,
                flag: actualFlag,
                applicantSignedAt: toIsoDate(merged.applicantSignedAt),
                reviewedBySignedAt: toIsoDate(merged.reviewedBySignedAt),
                patientSignedAt: toIsoDate(applicantSignedAtForPatient),
            };
            if (currentStatus === 'completed') {
                payload.flag = 'completed';
            }

            // Handle To Be Corrected comments
            const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
            if (finalComments) {
                payload.comments = finalComments;
            }
            const effectiveId = id || consentId;
            const url = effectiveId ? `/patient-consents/${effectiveId}` : '/patient-consents';
            const method = effectiveId ? 'PUT' : 'POST';

            const response = await request(url, {
                method: method,
                body: JSON.stringify(payload)
            });
            if (response.status === 1) {
                toast.success(response.message || `Patient Consent processed successfully!`);
                navigate("/general-forms/list?tab=patient-consent");
            } else {
                toast.error(response.message || "Failed to process form!");
            }
        } catch (error) {
            console.error("Error processing form:", error);
            toast.error(`Failed to process form: ${error.message}`);
        }
    };

    const {
        targetUserId,
        isSignatureModalOpen,
        openSignatureModal,
        closeSignatureModal,
        signerName,
        handleSignatureApply,
        handleStatusAction,
        handleSave,
        user: currentUserData,
        isAdmin,
        isReadOnly: hookReadOnly,
    } = useApplicationFormLogic({
        methods,
        applicantNameField: "employeeName",
        applicantSignatureField: "applicantSignature",
        applicantSignatureDateField: "applicantSignedAt",
        adminSignatureField: "reviewedBySignature",
        adminSignatureDateField: "reviewedBySignedAt",
        onClose,
        onSubmit,
        currentStatus
    });

    const isReadOnly = isAdmin ? false : hookReadOnly;

    const userFullName = currentUserData ? `${currentUserData.firstName || ""} ${currentUserData.lastName || ""}`.trim() || currentUserData.name || "" : "";

    useEffect(() => {
        const fetchPatientConsent = async () => {
            if (!isEditMode) return;
            try {
                const response = await request(`/patient-consents/${id}`);
                if (response.status === 1 && response.result?.patientConsent) {
                    const data = response.result.patientConsent;
                    setCurrentStatus(data.status || "");
                    reset({
                        ...data,
                        employeeName: data.employeeName || data.applicantName || userFullName,
                        applicantName: data.applicantName || data.employeeName || userFullName,
                        applicantSignedAt: parseIsoDateToMDYUS(data.applicantSignedAt),
                        reviewedBySignedAt: parseIsoDateToMDYUS(data.reviewedBySignedAt),
                        patientSignedAt: parseIsoDateToMDYUS(data.patientSignedAt),
                        ...getStandardizedSignaturesForGeneralForms(data, isAdmin)
                    });
                } else {
                    toast.error("Failed to load patient consent");
                }
            } catch (error) {
                console.error("Error loading patient consent:", error);
                toast.error("Error loading patient consent");
            }
        };

        const fetchByUserId = async () => {
            if (!targetUserId || !currentUserData) return;
            try {
                const response = await request(`/patient-consents?userId=${targetUserId}`);
                if (response.status === 1 && response.result?.items?.length > 0) {
                    const data = response.result.items[0];
                    setConsentId(data.id);
                    setCurrentStatus(data.status || "");
                    reset({
                        ...data,
                        employeeName: data.employeeName || data.applicantName || userFullName,
                        applicantName: data.applicantName || data.employeeName || userFullName,
                        applicantSignedAt: parseIsoDateToMDYUS(data.applicantSignedAt),
                        reviewedBySignedAt: parseIsoDateToMDYUS(data.reviewedBySignedAt),
                        patientSignedAt: parseIsoDateToMDYUS(data.patientSignedAt),
                        ...getStandardizedSignaturesForGeneralForms(data, isAdmin)
                    });
                }
            } catch (error) {
                console.error("Error fetching patient consent by userId:", error);
            }
        };

        if (isEditMode) {
            fetchPatientConsent();
        } else {
            fetchByUserId();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only form data load
    }, [isEditMode, id, targetUserId, reset, currentUserData]);

    const onCloseFrom = () => {
        if (isAdmin) {
            navigate("/general-forms/library");
        } else {
            navigate("/general-forms/list?tab=patient-consent");
        }
    }

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 font-sans">

            <CloseButton onClose={onCloseFrom} />
            <div className="max-w-5xl mx-auto bg-white shadow-xl rounded-xl overflow-hidden relative">

                <EvaluationFormHeader title="PATIENT CONSENT" />
                <fieldset disabled={isReadOnly} className="border-none p-0 m-0">
                    <div className="p-8 md:p-12">
                        <FormProvider {...methods}>
                            <form onSubmit={handleSubmit((data) => onSubmit(data))} className="space-y-6">

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-50 rounded-lg">
                                    <EvaluationInput name="employeeName" label="Employee Name" />
                                    <EvaluationInput name="patientName" label="Patient Name" />
                                </div>

                                <SectionHeader title="Consents" />
                                <div className="space-y-2">
                                    <CheckboxField name="consentAdmissionTreatment" label="Consent for Admission & Treatment" register={methods.register} />
                                    <CheckboxField name="consentAuthorizationReleaseInfo" label="Authorization for Release of Information" register={methods.register} />
                                    <CheckboxField name="consentPatientCertificationPayment" label="Patient Certification and Authorization to Payment" register={methods.register} />
                                    <CheckboxField name="consentHomeHealthBillingPps" label="Home Health Billing under PPS" register={methods.register} />
                                    <CheckboxField name="consentAssignmentInsuranceBenefits" label="Assignment of Insurance Benefits" register={methods.register} />
                                    <CheckboxField name="consentCmsAdvanceBeneficiaryNotice" label="CMS Advance Beneficiary Notice (ABN)" register={methods.register} />
                                </div>

                                <div className="ml-8 mt-2 space-y-2 border-l-2 pl-4 border-gray-300">
                                    <p className="text-sm font-semibold text-gray-600">Payment Options:</p>
                                    <CheckboxField name="paymentPrivateInsurance" label="Private Insurance" register={methods.register} />
                                    <CheckboxField name="paymentSelfPay" label="Self Pay" register={methods.register} />
                                </div>

                                <SectionHeader title="Visit Frequency" />
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <EvaluationInput name="snVisits" label="SN Visits" placeholder="e.g. 2x/week" />
                                    <EvaluationInput name="chhaVisits" label="CHHA Visits" placeholder="e.g. 3x/week" />
                                    <EvaluationInput name="ptVisits" label="PT Visits" placeholder="e.g. 2x/week" />
                                    <EvaluationInput name="otVisits" label="OT Visits" placeholder="e.g. 1x/week" />
                                    <EvaluationInput name="stVisits" label="ST Visits" />
                                    <EvaluationInput name="mswVisits" label="MSW Visits" placeholder="e.g. 1x/week" />
                                </div>

                                <SectionHeader title="Certifications & Acknowledgements" />
                                <div className="space-y-2">
                                    <CheckboxField name="certPatientsRightsResponsibilities" label="Patient's Rights & Responsibilities" register={methods.register} />
                                    <CheckboxField name="certOasisPrivacyHippa" label="OASIS Privacy Notice & HIPAA" register={methods.register} />
                                    <CheckboxField name="certStateJointCommissionHotline" label="State & Joint Commission Hotline" register={methods.register} />
                                    <CheckboxField name="certPatientsPrivacyRightsComplaint" label="Patient's Privacy Rights & Complaint Process" register={methods.register} />
                                    <CheckboxField name="certInitialPlanOfCareReviewed" label="Initial Plan of Care Reviewed" register={methods.register} />
                                    <CheckboxField name="certHomeHealthRatesReviewed" label="Home Health Worker Rates Reviewed" register={methods.register} />
                                    <CheckboxField name="certStateJointCommissionHotlineInformed" label="Informed of State/Joint Commission Hotline" register={methods.register} />
                                    <CheckboxField name="certEmergencyPlanSafetyInstructions" label="Emergency Plan & Safety Instructions" register={methods.register} />
                                    <CheckboxField name="certBrochureNonDiscriminatoryNotice" label="Brochure & Non-Discriminatory Notice" register={methods.register} />
                                    <CheckboxField name="certReceivedCopyOfDocument" label="Received Copy of Signed Document" register={methods.register} />
                                </div>

                                <SectionHeader title="Other" />
                                <div className="space-y-4">
                                    <div className="flex items-center gap-4">
                                        <label className="text-sm font-medium text-gray-700">Advance Directives:</label>
                                        <div className="flex items-center gap-4">
                                            <label className="flex items-center gap-2">
                                                <input type="radio" value="Yes" {...methods.register("advanceDirectives")} className="text-blue-600 focus:ring-blue-500" />
                                                <span className="text-sm">Yes</span>
                                            </label>
                                            <label className="flex items-center gap-2">
                                                <input type="radio" value="No" {...methods.register("advanceDirectives")} className="text-blue-600 focus:ring-blue-500" />
                                                <span className="text-sm">No</span>
                                            </label>
                                        </div>
                                    </div>
                                    <CheckboxField name="photoRecordsAuthorization" label="Authorization for Photos/Records" register={methods.register} />
                                </div>

                                <SectionHeader title="Patient Signature" />
                                <div className="mt-2">
                                    <EvaluationInput name="patientSignature" label="Patient/Representative Signature (Typed)" />
                                </div>

                                <GeneralFormFooter
                                    methods={methods}
                                    onSave={handleSave}
                                    onSubmit={(data, status) => onSubmit(data, status)}
                                    onStatusUpdate={handleStatusAction}
                                    isSignatureModalOpen={isSignatureModalOpen}
                                    openSignatureModal={openSignatureModal}
                                    closeSignatureModal={closeSignatureModal}
                                    handleSignatureApply={handleSignatureApply}
                                    signerName={signerName}
                                    employeeName={watch("employeeName") || ""}
                                    applicantSignature={watch("applicantSignature") || ""}
                                    applicantSignedAt={watch("applicantSignedAt") || ""}
                                    reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
                                    reviewedBySignature={watch("reviewedBySignature") || ""}
                                    reviewedBySignedAt={watch("reviewedBySignedAt") || ""}
                                    isReadOnly={isReadOnly}
                                    currentStatus={currentStatus}
                                    submitLabel="Submit Application"
                                />
                            </form>
                        </FormProvider>
                    </div>
                </fieldset>
            </div>
        </div>
    );
};

PatientConsent.propTypes = {
    onClose: PropTypes.func
};

export default PatientConsent;

